import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Alert,
    Paper,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    MenuItem,
    Select,
    FormControl,
    InputLabel
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { buildApiUrl } from '../config/api';

function AdminUsers() {
    const { token, user, fetchUser } = useAuth(); // Get current user info
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');

    // State for password change dialog
    const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [isChangingPassword, setIsChangingPassword] = useState(false);

    // Gerenciamento de usuários
    const [users, setUsers] = useState([]);
    const [userError, setUserError] = useState('');
    const [userSuccess, setUserSuccess] = useState('');
    const [newUsername, setNewUsername] = useState('');
    const [newRole, setNewRole] = useState('PESQUISA');
    const [isCreatingUser, setIsCreatingUser] = useState(false);

    useEffect(() => {
        if (token && !user?.username) {
            fetchUser();
        }
    }, [token, user, fetchUser]);

    const fetchUsers = useCallback(async () => {
        setUserError('');
        try {
            const response = await fetch(buildApiUrl('admin/users'), {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Erro ao buscar usuários');
            const data = await response.json();
            setUsers(data);
        } catch (err) {
            setUserError(err.message);
        }
    }, [token]);

    useEffect(() => {
        if (token && user?.role === 'ADMIN') fetchUsers();
    }, [token, user, fetchUsers]);

    const handleClosePasswordDialog = () => {
        setOpenPasswordDialog(false);
    };

    const handleChangePassword = async () => {
        setPasswordError('');
        setPasswordSuccess('');
        if (newPassword !== confirmNewPassword) {
            setPasswordError('As novas senhas não coincidem.');
            return;
        }
        if (!currentPassword || !newPassword) {
            setPasswordError('Todos os campos são obrigatórios.');
            return;
        }

        setIsChangingPassword(true);
        try {
            const response = await fetch(buildApiUrl('admin/users/me/password'), {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    current_password: currentPassword,
                    new_password: newPassword,
                }),
            });

            if (!response.ok) {
                 let errorMsg = `Erro ${response.status}`;
                 try {
                     const errorData = await response.json();
                     errorMsg = errorData.detail || errorMsg;
                 } catch (e) { /* Ignore */ }
                 throw new Error(errorMsg);
            }

            setPasswordSuccess('Senha alterada com sucesso!');
            handleClosePasswordDialog();
            // Optionally refetch user data if needed
            // fetchUser();
        } catch (err) {
            setPasswordError(err.message || 'Falha ao alterar a senha.');
        } finally {
            setIsChangingPassword(false);
        }
    };

    const handleCreateUser = async () => {
        setUserError(''); setUserSuccess('');
        if (!newUsername || !newPassword) {
            setUserError('Preencha todos os campos');
            return;
        }
        setIsCreatingUser(true);
        try {
            const response = await fetch(buildApiUrl('admin/users'), {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: newUsername, password: newPassword, role: newRole })
            });
            if (!response.ok) throw new Error('Erro ao criar usuário');
            setNewUsername(''); setNewPassword(''); setNewRole('PESQUISA');
            setUserSuccess('Usuário criado!');
            fetchUsers();
        } catch (err) { setUserError(err.message); }
        setIsCreatingUser(false);
    };

    const handleRoleChange = async (userId, role) => {
        setUserError(''); setUserSuccess('');
        try {
            const response = await fetch(buildApiUrl(`admin/users/${userId}/role?role=${role}`), {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error('Erro ao atualizar perfil');
            setUserSuccess('Perfil atualizado!');
            fetchUsers();
        } catch (err) { setUserError(err.message); }
    };

    return (
        <Box sx={{ mt: 4 }}>
            {user?.role === 'ADMIN' && (
                <>
                    <Typography variant="h5" gutterBottom>Gerenciar Usuários</Typography>
                    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>Criar Novo Usuário</Typography>
                        {userSuccess && <Alert severity="success" sx={{ mb: 2 }}>{userSuccess}</Alert>}
                        {userError && <Alert severity="error" sx={{ mb: 2 }}>{userError}</Alert>}
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <TextField label="Usuário" value={newUsername} onChange={e => setNewUsername(e.target.value)} size="small" />
                            <TextField label="Senha" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} size="small" />
                            <FormControl size="small">
                                <InputLabel id="role-label">Perfil</InputLabel>
                                <Select labelId="role-label" value={newRole} label="Perfil" onChange={e => setNewRole(e.target.value)}>
                                    <MenuItem value="ADMIN">ADMIN</MenuItem>
                                    <MenuItem value="PESQUISA">PESQUISA</MenuItem>
                                </Select>
                            </FormControl>
                            <Button variant="contained" onClick={handleCreateUser} disabled={isCreatingUser}>Criar</Button>
                        </Box>
                        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Usuários Cadastrados</Typography>
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Usuário</TableCell>
                                        <TableCell>Perfil</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Ações</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {users.map(u => (
                                        <TableRow key={u.id}>
                                            <TableCell>{u.username}</TableCell>
                                            <TableCell>
                                                <Select value={u.role} size="small" onChange={e => handleRoleChange(u.id, e.target.value)}>
                                                    <MenuItem value="ADMIN">ADMIN</MenuItem>
                                                    <MenuItem value="PESQUISA">PESQUISA</MenuItem>
                                                </Select>
                                            </TableCell>
                                            <TableCell>{u.is_active ? 'Ativo' : 'Inativo'}</TableCell>
                                            <TableCell></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </>
            )}
            <Typography variant="h5" gutterBottom>Alterar Senha</Typography>

            {passwordSuccess && <Alert severity="success" sx={{ mb: 2 }}>{passwordSuccess}</Alert>}

            <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Usuário Atual: {user?.username || 'N/A'}
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setOpenPasswordDialog(true)}
                    sx={{ mb: 2 }}
                >
                    Alterar Senha
                </Button>
            </Paper>

            {/* Password Change Dialog */}
            <Dialog open={openPasswordDialog} onClose={handleClosePasswordDialog}>
                <DialogTitle>Alterar Senha</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Para alterar sua senha, preencha os campos abaixo.
                    </DialogContentText>
                    {passwordError && <Alert severity="error" sx={{ mb: 2 }}>{passwordError}</Alert>}
                    <TextField
                        autoFocus
                        margin="dense"
                        id="currentPassword"
                        label="Senha Atual"
                        type="password"
                        fullWidth
                        variant="outlined"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        disabled={isChangingPassword}
                    />
                    <TextField
                        margin="dense"
                        id="newPassword"
                        label="Nova Senha"
                        type="password"
                        fullWidth
                        variant="outlined"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        disabled={isChangingPassword}
                    />
                    <TextField
                        margin="dense"
                        id="confirmNewPassword"
                        label="Confirmar Nova Senha"
                        type="password"
                        fullWidth
                        variant="outlined"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        disabled={isChangingPassword}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenPasswordDialog(false)}>Cancelar</Button>
                    <Button onClick={handleChangePassword} disabled={isChangingPassword || !currentPassword || !newPassword || !confirmNewPassword} variant="contained" color="primary">
                        Salvar Nova Senha
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default AdminUsers;

